import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { api } from '../api'
import { formatDate } from '../lib/format'
import { plural } from '../lib/format'
import type { SummaryTicket } from '../types'

/**
 * Сводка по работе.
 *
 * Отвечает на вопросы, которые иначе задают людям: что висит, у кого
 * просрочено, что сделано и не оплачено, кому не ответили. Числа наверху —
 * чтобы понять положение за несколько секунд; списки ниже — чтобы сразу
 * открыть то, чем надо заняться.
 *
 * Списки короткие намеренно. Больше десятка строк никто не разбирает за раз,
 * а длинный список превращает сводку в ещё один журнал.
 */
export default function Summary() {
  const nav = useNavigate()
  const data = useQuery({
    queryKey: ['summary'],
    queryFn: api.summary,
    refetchInterval: 60_000,
  })

  const s = data.data

  function open(t: SummaryTicket) {
    nav(t.kind === 'order' ? '/orders' : '/tickets')
  }

  return (
    <>
      <h1 className="page-title">Сводка</h1>

      {data.isLoading && <p className="empty">Загрузка…</p>}

      {s && (
        <>
          <div className="pv-cards">
            <Card
              label="Ждут нашего ответа"
              value={s.waiting_for_us}
              sub="клиент написал, мы молчим"
              alarm={s.waiting_for_us > 0}
            />
            <Card
              label="Просрочено"
              value={s.overdue}
              sub="срок прошёл, работа не закончена"
              alarm={s.overdue > 0}
            />
            <Card
              label="Сделано, не оплачено"
              value={s.done_unpaid}
              sub="работа закончена, денег нет"
              alarm={s.done_unpaid > 0}
            />
            <Card
              label="Ждут согласования"
              value={s.waiting_approval}
              sub="оценка есть, к клиенту не пошли"
              alarm={s.waiting_approval > 0}
            />
          </div>

          <div className="pv-cards" style={{ marginTop: 12 }}>
            <Card
              label="Никем не взято"
              value={s.unassigned}
              sub="нет ответственного"
              alarm={s.unassigned > 0}
            />
            <Card
              label="У специалиста"
              value={s.at_specialist}
              sub="клиент назвал своё число, ждём ответа"
            />
          </div>

          <div className="pv-cards" style={{ marginTop: 12 }}>
            <Card label="Открытых обращений" value={s.open_requests} sub="сопровождение и аренда" />
            <Card label="Открытых заявок" value={s.open_orders} sub="коды и работы" />
            <Card
              label="Кодов за 30 дней"
              value={s.codes_last_30_days}
              sub={plural(s.codes_last_30_days, 'код заказан', 'кода заказано', 'кодов заказано')}
            />
            <Card
              label="Бесед без ответа"
              value={s.conversations_waiting}
              sub="последним писал клиент"
              alarm={s.conversations_waiting > 0}
            />
          </div>

          <div className="grid-2" style={{ marginTop: 18, alignItems: 'start' }}>
            <Breakdown title="Заявки по этапам" rows={s.orders_by_stage} />
            <Breakdown title="Обращения по видам" rows={s.requests_by_category} />
          </div>

          {s.tariffs_running_out.length > 0 && (
            <>
              <h2 className="section-title">Тарифы на исходе</h2>
              <p className="hint">
                Единственное место, где о подписке вспоминают заранее, а не когда
                клиент упёрся в исчерпанные часы.
              </p>
              <div className="table-wrap">
                <table>
                  <tbody>
                    {s.tariffs_running_out.map((r) => (
                      <tr key={r.title}>
                        <td>{r.title}</td>
                        <td className="cell-sub">{r.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <Listing title="Ждут согласования с клиентом" rows={s.approval_pending} onOpen={open} />
          <Listing title="Дольше всех ждут ответа" rows={s.oldest_waiting} onOpen={open} />
          <Listing title="Просроченные заявки" rows={s.overdue_orders} onOpen={open} />
          <Listing title="Сделано, но не оплачено" rows={s.unpaid_orders} onOpen={open} />
          <Listing title="Никем не взято" rows={s.unassigned_tickets} onOpen={open} />
        </>
      )}
    </>
  )
}

function Card({
  label,
  value,
  sub,
  alarm,
}: {
  label: string
  value: number
  sub: string
  alarm?: boolean
}) {
  return (
    <div className="pv-card">
      <div className="pv-label">{label}</div>
      {/* Красным только то, что требует действия: если красное всё, оно
          перестаёт что-либо значить. */}
      <div className="pv-value" style={alarm ? { color: '#b91c1c' } : undefined}>
        {value}
      </div>
      <div className="pv-sub">{sub}</div>
    </div>
  )
}

function Breakdown({ title, rows }: { title: string; rows: { title: string; detail: string }[] }) {
  if (rows.length === 0) return null
  return (
    <div>
      <h2 className="section-title">{title}</h2>
      <div className="table-wrap">
        <table>
          <tbody>
            {rows.map((r) => (
              <tr key={r.title}>
                <td>{r.title}</td>
                <td className="num">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Listing({
  title,
  rows,
  onOpen,
}: {
  title: string
  rows: SummaryTicket[]
  onOpen: (t: SummaryTicket) => void
}) {
  if (rows.length === 0) return null
  return (
    <>
      <h2 className="section-title">{title}</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="col-num">№</th>
              <th>Клиент</th>
              <th>Тема</th>
              <th>Вид</th>
              <th>Ведёт</th>
              <th>Срок</th>
              <th className="num">Дней</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={`${t.kind}-${t.id}`}>
                <td className="task-num">{t.id}</td>
                <td className="cell-client">
                  <span className="client-name">{t.client_name}</span>
                </td>
                <td>
                  <button className="link-btn" onClick={() => onOpen(t)}>
                    {t.subject}
                  </button>
                </td>
                <td className="nowrap">
                  <span className="tk-cat-chip">{t.category_title}</span>
                  {t.stage_title && <span className="cell-sub">{t.stage_title}</span>}
                </td>
                <td>
                  {t.assignee_name ?? '—'}
                  {t.approval_title && <span className="cell-sub">{t.approval_title}</span>}
                  {t.estimate_hours !== null && (
                    <span className="cell-sub">оценка {t.estimate_hours} ч</span>
                  )}
                </td>
                <td className="nowrap">{t.due_on ? formatDate(t.due_on) : '—'}</td>
                <td className="num">{t.days_open}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
